<template>
  <!--
    Lays out the information the unit_view.partial search column is built from, grouped so it can
    be scanned: what the unit is, which building it is in, and who owns it. Tenants are left out
    deliberately - an expense can be charged to a unit that has no contract, so the unit, building
    and owner are what identify it. Primary keys are shown in brackets rather than legacy reknos.
  -->
  <div class="flex flex-col gap-0.5 leading-tight py-0.5">
    <!-- What -->
    <div class="font-semibold">
      <span v-if="props.data.UnitReference">{{ props.data.UnitReference }}</span>
      <span v-else class="italic text-gray-400">Unit {{ props.data.Id }}</span>
      <span v-if="props.data.Id" class="ml-1 font-normal text-gray-400">
        (Unit {{ props.data.Id }})
      </span>
      <span v-if="props.data.StandNumber" class="ml-1 font-normal text-gray-500">
        &middot; Stand {{ props.data.StandNumber }}
      </span>
      <span
        v-if="props.data.UnitStatusDescription"
        class="ml-1 font-normal text-gray-400"
      >
        &middot; {{ props.data.UnitStatusDescription }}
      </span>
    </div>

    <!-- Where: building and its address -->
    <div v-if="props.data.ComplexDescription" class="text-sm text-gray-600">
      Building: {{ props.data.ComplexDescription }}
      <span v-if="props.data.ComplexId" class="text-gray-400">
        ({{ props.data.ComplexId }})
      </span>
    </div>
    <div v-if="buildingAddress" class="text-sm text-gray-500">
      {{ buildingAddress }}
    </div>

    <!-- Who owns it -->
    <div v-if="ownerName" class="text-sm text-gray-500">
      Owner: {{ ownerName }}
      <span v-if="props.data.OwnerId" class="text-gray-400">
        ({{ props.data.OwnerId }})
      </span>
    </div>
    <div v-if="ownerContact" class="text-sm text-gray-400">
      {{ ownerContact }}
    </div>
  </div>
</template>

<script setup lang="ts">
interface AuxInputProps {
  data: any;
}

const props = defineProps<AuxInputProps>();

const join = (parts: any[], separator: string) =>
  parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter((p) => p !== "")
    .join(separator);

// unit_view exposes OwnerNames (plural) and OwnerSurname - there is no OwnerName column.
const ownerName = computed(() =>
  join([props.data?.OwnerNames, props.data?.OwnerSurname], " ")
);

// The complex address lines are the building's address.
const buildingAddress = computed(() =>
  join(
    [
      props.data?.ComplexAddressLine1,
      props.data?.ComplexAddressLine2,
      props.data?.ComplexAddressLine3,
    ],
    ", "
  )
);

const ownerContact = computed(() => {
  const parts = [];
  if (props.data?.OwnerIdNumber) parts.push(`ID: ${props.data.OwnerIdNumber}`);
  if (props.data?.OwnerCellPhoneNumber)
    parts.push(`Cell: ${props.data.OwnerCellPhoneNumber}`);
  return parts.join(" | ");
});
</script>

<style scoped></style>
